import { useState, useEffect, useCallback, useRef } from 'react';

const DB_NAME = 'adhd-blocknotes';
const DB_VERSION = 1;
const STORE_NAME = 'notes';

function openNotesDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
        store.createIndex('tags', 'tags', { unique: false, multiEntry: true });
      }
    };
  });
}

function dbGet(db, storeName, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function dbGetAll(db, storeName) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

function dbPut(db, storeName, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).put(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function dbDelete(db, storeName, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).delete(key);
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
}

export function createNewNote(overrides = {}) {
  return {
    id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    title: '',
    blocks: [{ id: `b-${Date.now()}`, type: 'paragraph', content: '' }],
    audioChunks: null,   // base64 webm
    videoChunks: null,   // base64 webm
    transcript: '',
    tags: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...overrides,
  };
}

export function useBlockNotes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const dbRef = useRef(null);

  useEffect(() => {
    openNotesDB()
      .then((db) => {
        dbRef.current = db;
        return dbGetAll(db, STORE_NAME);
      })
      .then((allNotes) => {
        // Sort by updatedAt desc
        const sorted = [...allNotes].sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
        setNotes(sorted);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[useBlockNotes] DB init error:', err);
        setLoading(false);
      });
  }, []);

  const saveNote = useCallback(async (note) => {
    if (!dbRef.current) return null;
    const updated = { ...note, updatedAt: new Date().toISOString() };
    try {
      await dbPut(dbRef.current, STORE_NAME, updated);
      setNotes((prev) => {
        const idx = prev.findIndex((n) => n.id === updated.id);
        const next = idx >= 0 ? [...prev] : [updated, ...prev];
        if (idx >= 0) next[idx] = updated;
        return next.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      });
      return updated;
    } catch (err) {
      console.error('[useBlockNotes] saveNote error:', err);
      return null;
    }
  }, []);

  const deleteNote = useCallback(async (noteId) => {
    if (!dbRef.current) return false;
    try {
      await dbDelete(dbRef.current, STORE_NAME, noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      return true;
    } catch (err) {
      console.error('[useBlockNotes] deleteNote error:', err);
      return false;
    }
  }, []);

  const getNote = useCallback(async (noteId) => {
    if (!dbRef.current) return null;
    return dbGet(dbRef.current, STORE_NAME, noteId);
  }, []);

  return { notes, loading, saveNote, deleteNote, getNote, createNewNote };
}
